import { DateTime } from "luxon";
import { isBeforeToday, toFormat } from "@/lib/dateHelpers";
import { Person, PlayingAbilityTestStatus, ProgramStatus, ProgramTypeName } from "@/types";
import {
  getProspectAssociateProgramDeadline,
  hasStartedPgmLevelOne,
  isPgmEducationProgramComplete,
  isQualifyingLevelComplete
} from "./pgm-levels";

export type PathAwareStatusTooltip = {
  title: string;
  supportingText: string;
};

type PathStillMetReason =
  | "active_membership"
  | "completed_pgm_program"
  | "acceptable_pgm_progress"
  | "past_qualifying_level"
  | "approved_associate_application";

function parseDateOnly(date: string | null | undefined): DateTime | null {
  if (!date) return null;
  const parsed = DateTime.fromISO(date);
  return parsed.isValid ? parsed.startOf("day") : null;
}

function isDateOnOrBefore(left: string | null | undefined, right: string | null | undefined): boolean {
  const leftDate = parseDateOnly(left);
  const rightDate = parseDateOnly(right);
  if (!leftDate || !rightDate) return false;
  return leftDate <= rightDate;
}

function isApprovedApplicationStatus(status?: string | null): boolean {
  const normalized = status?.toLowerCase();
  return normalized === "approved" || normalized === "elected" || normalized === "complete";
}

function hasApprovedAssociateApplication(person: Person): boolean {
  const membershipApp = (person.membershipApplications ?? []).find(
    (application) =>
      application.applicationType?.toLowerCase() === "associate" && isApprovedApplicationStatus(application.status)
  );
  if (membershipApp) return true;
  return isApprovedApplicationStatus(person.prospectProgram?.latestAssociateApplication?.status);
}

function getLevelOneStartDate(person: Person): string | null {
  const progressStart = person.pgmProgressStatus?.levels.find((level) => level.numeral === 1)?.startDate;
  if (progressStart) return progressStart;

  if (person.educationLatestPgmProgram?.level === 1 && person.educationLatestPgmProgram.startDate) {
    return person.educationLatestPgmProgram.startDate;
  }

  const historyLevelOne = [...(person.educationPgmProgramHistory ?? [])]
    .filter((item) => item.level === 1 && !!item.startDate)
    .sort((a, b) => {
      const aDate = parseDateOnly(a.startDate)?.toMillis() ?? 0;
      const bDate = parseDateOnly(b.startDate)?.toMillis() ?? 0;
      return bDate - aDate;
    })[0];

  return historyLevelOne?.startDate ?? null;
}

function getLatestLevelOnePlusProgram(person: Person) {
  const candidates = [
    ...(person.educationLatestPgmProgram?.level && person.educationLatestPgmProgram.level >= 1
      ? [person.educationLatestPgmProgram]
      : []),
    ...(person.educationPgmProgramHistory ?? []).filter((item) => (item.level ?? 0) >= 1)
  ];

  return [...candidates].sort((a, b) => {
    const aDate = parseDateOnly(a.startDate)?.toMillis() ?? 0;
    const bDate = parseDateOnly(b.startDate)?.toMillis() ?? 0;
    return bDate - aDate;
  })[0];
}

function isCurrentMembershipOnPgmPath(person: Person): boolean {
  const programName = person.latestProgram?.programType?.name;
  if (!programName) return false;

  return (
    programName === ProgramTypeName.Associate ||
    programName === ProgramTypeName.Affiliate ||
    programName === ProgramTypeName.Member
  );
}

function latestEducationHistoryProgram(person: Person) {
  return [...(person.educationPgmProgramHistory ?? [])].sort((a, b) => {
    const aDate = parseDateOnly(a.startDate)?.toMillis() ?? 0;
    const bDate = parseDateOnly(b.startDate)?.toMillis() ?? 0;
    return bDate - aDate;
  })[0];
}

function currentEducationProgramLevel(person: Person): number | null {
  const latestLevel = person.educationLatestPgmProgram?.level;
  if (typeof latestLevel === "number") return latestLevel;

  const currentKey = person.pgmProgressStatus?.currentLevelKey;
  if (currentKey) {
    const current = person.pgmProgressStatus?.levels.find((level) => level.key === currentKey);
    if (typeof current?.numeral === "number") return current.numeral;
  }

  const historyLatest = latestEducationHistoryProgram(person);
  return typeof historyLatest?.level === "number" ? historyLatest.level : null;
}

function isCurrentEducationProgramLevelOnePlus(person: Person): boolean {
  const level = currentEducationProgramLevel(person);
  return level != null && level >= 1;
}

function isOnCurrentPgmEducationPath(person: Person): boolean {
  if (!hasStartedPgmLevelOne(person)) return false;
  if (!isCurrentMembershipOnPgmPath(person)) return false;
  // GraphQL latestProgram prefers Associate over Prospect, so a QL restart can
  // still look like Associate. Current education level is the in-PGM signal.
  if (!isCurrentEducationProgramLevelOnePlus(person)) return false;
  return getLatestLevelOnePlusProgram(person) != null;
}

function latestPgmProgramIsComplete(person: Person): boolean {
  return isPgmEducationProgramComplete(person);
}

function isPgmProgramComplete(person: Person): boolean {
  return isOnCurrentPgmEducationPath(person) && latestPgmProgramIsComplete(person);
}

function isPgmAcceptableProgressActive(person: Person): boolean {
  if (!isOnCurrentPgmEducationPath(person)) return false;
  if (latestPgmProgramIsComplete(person)) return true;

  const latest = getLatestLevelOnePlusProgram(person);
  if (!latest) return false;

  const expiration = latest.pgmProgramExpirationDate ?? null;
  if (!expiration) return false;

  return !isBeforeToday(expiration);
}

function resolvePgmPathStillMetReason(person: Person): PathStillMetReason | null {
  if (!isPgmAcceptableProgressActive(person)) return null;
  return isPgmProgramComplete(person) ? "completed_pgm_program" : "acceptable_pgm_progress";
}

function isActiveMember(person: Person): boolean {
  return (
    person.latestProgram?.programType?.name === ProgramTypeName.Member &&
    person.latestProgram?.status !== ProgramStatus.TERMINATED
  );
}

function isActiveAssociateOrAffiliatePastQualifying(person: Person): boolean {
  const programName = person.latestProgram?.programType?.name;
  const programStatus = person.latestProgram?.status;
  if (!programName || programStatus === ProgramStatus.TERMINATED) return false;
  if (programName !== ProgramTypeName.Associate && programName !== ProgramTypeName.Affiliate) return false;
  if (hasStartedPgmLevelOne(person)) return false;

  return isQualifyingLevelComplete(person);
}

function isActivelyFinishingWithApprovedAssociateApp(person: Person): boolean {
  if (!hasApprovedAssociateApplication(person)) return false;
  if (hasStartedPgmLevelOne(person)) return false;

  const programName = person.latestProgram?.programType?.name;
  const programStatus = person.latestProgram?.status;
  if (!programName || programStatus === ProgramStatus.TERMINATED) return false;
  if (programName === ProgramTypeName.Affiliate || programName === ProgramTypeName.Member) return false;

  return isQualifyingLevelComplete(person);
}

function resolvePathStillMetReason(
  person: Person,
  options?: { includeApprovedAssociateApp?: boolean }
): PathStillMetReason | null {
  if (isActiveMember(person)) return "active_membership";
  const pgmReason = resolvePgmPathStillMetReason(person);
  if (pgmReason) return pgmReason;
  if (isActiveAssociateOrAffiliatePastQualifying(person)) return "past_qualifying_level";
  // BGC-only: PAT is not a Qualifying Level requirement, so finishing-QL approved-app
  // still-met does not apply to Playing Ability Test credentials.
  if (options?.includeApprovedAssociateApp && isActivelyFinishingWithApprovedAssociateApp(person)) {
    return "approved_associate_application";
  }
  return null;
}

function stillMetClause(credentialNoun: "check" | "PAT", reason: PathStillMetReason): string {
  switch (reason) {
    case "active_membership":
      return `No new ${credentialNoun} required while this person holds active membership.`;
    case "completed_pgm_program":
      return `No new ${credentialNoun} required because this person completed the PGM program.`;
    case "acceptable_pgm_progress":
      return `No new ${credentialNoun} required while this person remains in acceptable PGM progress.`;
    case "past_qualifying_level":
      return `No new ${credentialNoun} required while this person remains past Qualifying Level on an active Associate or Affiliate path.`;
    case "approved_associate_application":
      return `No new ${credentialNoun} required while this person has an approved Associate application and is finishing Qualifying Level.`;
  }
}

export function isBackgroundCheckStillMetForCurrentPath(person: Person): boolean {
  const status = person.prospectProgram?.backgroundCheck?.status?.toUpperCase();
  if (status !== "EXPIRED" && status !== "APPROVED") return false;
  if (status === "APPROVED") return true;

  return resolvePathStillMetReason(person, { includeApprovedAssociateApp: true }) != null;
}

export function buildBackgroundCheckPathAwareTooltip(options: {
  stillMet: boolean;
  expiredDate?: string | null;
  person?: Person;
}): PathAwareStatusTooltip | undefined {
  const formattedExpired = options.expiredDate ? toFormat(options.expiredDate) : null;
  if (!formattedExpired) return undefined;

  if (options.stillMet) {
    const reason =
      (options.person && resolvePathStillMetReason(options.person, { includeApprovedAssociateApp: true })) ??
      "acceptable_pgm_progress";
    return {
      title: "Background Check",
      supportingText: `Credential expired ${formattedExpired}. ${stillMetClause("check", reason)} A new check is required if they leave that path and must restart as a prospect.`
    };
  }

  return {
    title: "Background Check",
    supportingText: `Credential expired ${formattedExpired}. A new background check is required.`
  };
}

function resolvePatStillMetReason(
  person: Person,
  expirationDate: string | null | undefined
): PathStillMetReason | null {
  if (isActiveMember(person)) return "active_membership";
  // Past QL before L1 does not need Level 1 timing; check before the L1 date gate.
  if (isActiveAssociateOrAffiliatePastQualifying(person)) return "past_qualifying_level";

  const levelOneStart = getLevelOneStartDate(person);
  if (!isDateOnOrBefore(levelOneStart, expirationDate)) return null;

  return resolvePgmPathStillMetReason(person);
}

export function isPatCredentialStillMetForCurrentPath(
  person: Person,
  patData: PlayingAbilityTestStatus | null | undefined
): boolean {
  if (!patData?.expired) return false;
  return resolvePatStillMetReason(person, patData.expirationDate) != null;
}

export function isPatSubmissionStillMetForCurrentPath(
  person: Person,
  submission: { expired?: boolean; expirationDate?: string | null }
): boolean {
  if (!submission.expired) return true;
  return resolvePatStillMetReason(person, submission.expirationDate) != null;
}

export function buildPlayingAbilityTestPathAwareTooltip(options: {
  stillMet: boolean;
  expiredDate?: string | null;
  person?: Person;
}): PathAwareStatusTooltip | undefined {
  const formattedExpired = options.expiredDate ? toFormat(options.expiredDate) : null;
  if (!formattedExpired) return undefined;

  if (options.stillMet) {
    const reason =
      (options.person && resolvePatStillMetReason(options.person, options.expiredDate)) ?? "acceptable_pgm_progress";
    return {
      title: "Playing Ability Test",
      supportingText: `Credential expired ${formattedExpired}. ${stillMetClause("PAT", reason)}`
    };
  }

  return {
    title: "Playing Ability Test",
    supportingText: `Credential expired ${formattedExpired}. A new Playing Ability Test is required.`
  };
}

/**
 * QE / 18-month Associate-application clock. Prefer the same `expiredAt` Prospect
 * Program shows on Qualifying Level Courses, then the computed prospect deadline.
 */
export function getQualifyingLevelStandingExpirationDate(person: Person): string | null {
  const fromQualifyingLevel = person.prospectProgram?.qualifyingLevel?.expirationDate;
  if (fromQualifyingLevel) return fromQualifyingLevel.split("T")[0];

  return getProspectAssociateProgramDeadline(person);
}

export function isQualifyingLevelStandingExpiredForProspectRestart(person: Person): boolean {
  if (!isQualifyingLevelComplete(person)) return false;
  if (hasStartedPgmLevelOne(person)) return false;

  const programName = person.latestProgram?.programType?.name;
  if (
    programName === ProgramTypeName.Associate ||
    programName === ProgramTypeName.Affiliate ||
    programName === ProgramTypeName.Member
  ) {
    return false;
  }

  return isBeforeToday(getQualifyingLevelStandingExpirationDate(person));
}

export function buildQualifyingLevelRestartTooltip(options: {
  title: string;
  redoClause: string;
  expiredDate?: string | null;
}): PathAwareStatusTooltip | undefined {
  const formattedExpired = options.expiredDate ? toFormat(options.expiredDate) : null;
  if (!formattedExpired) return undefined;

  return {
    title: options.title,
    supportingText: `Standing expired ${formattedExpired}. ${options.redoClause}`
  };
}
